import { useAppDispatch, useAppSelector } from 'shared/hooks';
import { SIDEBAR_ITEMS, SidebarItem, SubSidebarItem } from './constants';
import * as Styled from './styles';
import { Button, Menu, Tooltip } from 'antd';
import { useLocation } from 'react-router';
import { NavLink, useNavigate } from 'react-router-dom';
import Logo from 'assets/images/curio-xr-logo.png';
import { logoutUser } from 'services/store/reducers/authReducer';
import { closeSidebar } from 'services/store/reducers/sidebarReducer';
import { clearSchools } from 'services/store/reducers/schoolsReducer';
import { Admin } from 'shared/types';
import { TIMEZONES } from 'pages/settings-page/constants';
import Icon from '@ant-design/icons';
import { ReactComponent as LogoutIcon } from 'assets/images/logout.svg';
import React from 'react';

const EllipsisTooltip: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children
}) => {
  return (
    <Tooltip title={title} placement='right'>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          width: '100%'
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

const Sidebar = (): JSX.Element => {
  const isSidebarOpened = useAppSelector((state): boolean => state.sidebar.isSidebarOpened);
  const user = useAppSelector((state): Admin | null => state.auth.user);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const logOut = (): void => {
    dispatch(clearSchools());
    dispatch(logoutUser());
    dispatch(closeSidebar());
    navigate('/d/login', { replace: true });
  };

  const handleMenuItemClick = (link: string): void => {
    dispatch(closeSidebar());
    navigate(link);
  };

  const handleCloseButtonClick = (): void => {
    dispatch(closeSidebar());
  };

  const allowedSidebarItems = SIDEBAR_ITEMS.filter((item) =>
    item.allowedRoles.some((role) => role === user?.role)
  );

  const generateMenuItems = (items: (SidebarItem | SubSidebarItem)[] | undefined): any[] => {
    if (!items) return [];

    return items
      .filter((item) => item.allowedRoles.some((role) => role === user?.role))
      .map((item) => {
        const hasSubItems = !!item.subItems?.length;
        const isUserReport = item.title === 'School Users Report';

        const labelContent = (
          <EllipsisTooltip title={item.title}>
            <div
              style={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }}
              onClick={
                isUserReport && hasSubItems
                  ? (e) => {
                      e.stopPropagation();
                      handleMenuItemClick(item.link);
                    }
                  : undefined
              }
            >
              {item.title}
            </div>
          </EllipsisTooltip>
        );

        return {
          key: item.link,
          icon: 'icon' in item && item.icon ? <item.icon /> : undefined,
          label: labelContent,
          children: hasSubItems ? generateMenuItems(item.subItems) : undefined,
          onClick: !hasSubItems ? () => handleMenuItemClick(item.link) : undefined
        };
      });
  };

  const menuItems = generateMenuItems(allowedSidebarItems);

  return (
    <>
      <Styled.SidebarContainer $isSidebarOpened={isSidebarOpened}>
        <Styled.CloseButton onClick={handleCloseButtonClick}>
          <Styled.CloseButton>
            <Icon component={LogoutIcon} />
          </Styled.CloseButton>
        </Styled.CloseButton>
        <Styled.SidebarContent>
          <Styled.SidebarLogo>
            <NavLink to='/d/video-tour'>
              <img src={Logo} alt='Logo' />
              <Styled.SidebarLogoTitle>
                {process.env.REACT_APP_SITE_TITLE || 'Dashboard'}
              </Styled.SidebarLogoTitle>
            </NavLink>
          </Styled.SidebarLogo>
          <Styled.SidebarItems>
            <Menu
              mode='inline'
              theme='light'
              defaultSelectedKeys={['/']}
              selectedKeys={[location.pathname]}
              items={menuItems}
            />
          </Styled.SidebarItems>
        </Styled.SidebarContent>
        <Styled.SidebarFooter>
          <Styled.SidebarUserInfo>
            <div>Logged in as</div>
            <div>{user?.email}</div>
          </Styled.SidebarUserInfo>
          <Styled.SidebarUserActions>
            <Button type='primary' onClick={logOut}>
              <Icon component={LogoutIcon} />
              <span>Logout</span>
            </Button>
            <Styled.TimezoneLabel>
              <Tooltip title='Change timezone in Settings' placement='top'>
                <Styled.TimezoneLink to='/d/settings' onClick={handleCloseButtonClick}>
                  All times in{' '}
                  {TIMEZONES.find((tz) => tz.value === `${user?.timezone}`)?.abbreviation ?? 'UTC'}
                </Styled.TimezoneLink>
              </Tooltip>
            </Styled.TimezoneLabel>
          </Styled.SidebarUserActions>
        </Styled.SidebarFooter>
      </Styled.SidebarContainer>
      <Styled.Overlay $isSidebarOpened={isSidebarOpened} onClick={handleCloseButtonClick} />
    </>
  );
};

export default Sidebar;
